/**
 * Go-forward hook for reading the authenticated user in components — replaces
 * direct `useAuthStore` selector use.
 *
 * Architecture: the `['auth','me']` TanStack Query (owned by `useAuthQuery`,
 * mounted in App) is the SOURCE — it fetches and self-refreshes on
 * staleTime/focus (the fix for the stale-user bug). Its result is mirrored into
 * the Zustand auth store (see the cache→store sync in useAuthStore), which is a
 * provider-independent, reliably-reactive projection. This hook reads that
 * projection so a component re-renders on every auth change — login, logout,
 * context switch, or a background refetch — regardless of which QueryClient the
 * surrounding provider holds (a plain query observer does not re-render across a
 * mismatched provider, e.g. in tests).
 *
 * Role checks are BOOLEANS here (not functions): `isStaff`, not `isStaff()`.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/useAuthStore';
import { hasSession } from '@/auth/session';
import { queryClient } from '@/api/queryClient';
import * as auth from '@/auth/authData';
import { AUTH_ME_KEY, authMeQueryOptions } from '@/auth/authData';
import type { User, UserContext } from '@/types';

export interface CurrentUser {
  user: User | null;
  /** A user has been loaded. */
  isAuthenticated: boolean;
  /** A session token exists (may be true before the user has loaded). */
  hasSession: boolean;
  /** A session token exists but the user hasn't loaded yet (bootstrap fetch). */
  isLoadingUser: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isViewer: boolean;
  isStaff: boolean;
  isSuperuser: boolean;
  canSwitchContext: boolean;
  currentContext: UserContext | null;
  requiresContextSelection: boolean;
  /**
   * The error from the `['auth','me']` query when it has no data (bootstrap
   * failure). null while loading or after success. Read from the module
   * queryClient via a disabled observer — the App-mounted `useAuthQuery` owns
   * fetching; this observer only reads (#447).
   */
  userLoadError: unknown | null;
  /** Force a refresh of /auth/me. */
  refetch: () => void;
}

export function useCurrentUser(): CurrentUser {
  const user = useAuthStore((s) => s.user);
  const session = hasSession();

  // #447: subscribe to the auth/me query's error state via a disabled observer
  // pinned to the module queryClient. The App-mounted useAuthQuery owns
  // fetching; this observer only reads so ProtectedRoute can distinguish
  // "loading" from "failed" and render the UnreachableState instead of null.
  //
  // #479: spread the canonical authMeQueryOptions (same queryFn as useAuthQuery)
  // so that if TanStack Query copies this observer's options onto the shared
  // Query, invalidateQueries-driven refetches still hit GET /api/auth/me/.
  const { error: meQueryError, data: meQueryData } = useQuery<User>({
    ...authMeQueryOptions(),
    enabled: false,
  }, queryClient);

  // Expose the error only when there is no data (bootstrap failure). A
  // background refetch failure after a successful initial load has data in
  // the cache and should not show the unreachable state.
  const userLoadError = !meQueryData && meQueryError ? meQueryError : null;

  return {
    user,
    isAuthenticated: !!user,
    hasSession: session,
    isLoadingUser: session && !user,
    userLoadError,
    isAdmin: auth.isAdmin(user),
    isManager: auth.isManager(user),
    isViewer: auth.isViewer(user),
    isStaff: auth.isStaff(user),
    isSuperuser: auth.isSuperuser(user),
    canSwitchContext: auth.canSwitchContext(user),
    currentContext: auth.currentContext(user),
    requiresContextSelection: auth.requiresContextSelection(user),
    refetch: () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_KEY });
    },
  };
}
